import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtEUR, MONTHLY_LIMIT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    FilePdf,
    FileXls,
    Receipt,
    CaretDown,
    CaretUp,
    X,
    Phone,
    EnvelopeSimple,
    MapPin,
    IdentificationCard,
    Note,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { exportCustomerPDF, exportCustomerXLSX } from "@/lib/export-customer";

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleString("el-GR", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "";

export default function CustomerDetailPanel({ customer, onClose }) {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    // Range selector: "last" = last 30 days | "prev" = previous calendar month
    // | "custom" = a specific month picked from the dropdown | "all" = everything
    const [range, setRange] = useState("last");
    const [customMonth, setCustomMonth] = useState(""); // "YYYY-MM"

    useEffect(() => {
        if (!customer?.id) return;
        setLoading(true);
        api.get(`/customers/${customer.id}/sales`)
            .then(({ data }) => setSales(data))
            .catch(() => toast.error("Αποτυχία φόρτωσης κινήσεων"))
            .finally(() => setLoading(false));
    }, [customer?.id]);

    const GREEK_MONTHS = [
        "ΙΑΝΟΥΑΡΙΟΣ", "ΦΕΒΡΟΥΑΡΙΟΣ", "ΜΑΡΤΙΟΣ", "ΑΠΡΙΛΙΟΣ", "ΜΑΪΟΣ", "ΙΟΥΝΙΟΣ",
        "ΙΟΥΛΙΟΣ", "ΑΥΓΟΥΣΤΟΣ", "ΣΕΠΤΕΜΒΡΙΟΣ", "ΟΚΤΩΒΡΙΟΣ", "ΝΟΕΜΒΡΙΟΣ", "ΔΕΚΕΜΒΡΙΟΣ",
    ];

    // Build a list of the last 24 calendar months, skipping the current one
    // (since it's already covered by "ΤΕΛΕΥΤΑΙΟΣ ΜΗΝΑΣ") and the previous one
    // (covered by "ΠΡΟΗΓΟΥΜΕΝΟΣ ΜΗΝΑΣ" — but we still include it so the
    // dropdown is self-contained if someone prefers this UX).
    const pastMonths = useMemo(() => {
        const out = [];
        const now = new Date();
        for (let i = 1; i <= 24; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            out.push({ key, label: `${GREEK_MONTHS[d.getMonth()]} ${d.getFullYear()}` });
        }
        return out;
    }, []);

    const lastMonthSales = useMemo(() => {
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        return sales.filter((s) => new Date(s.created_at).getTime() >= cutoff);
    }, [sales]);

    // Previous calendar month (1st 00:00 → last day 23:59:59 of the month
    // immediately before the current one).
    const prevMonthSales = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return sales.filter((s) => {
            const t = new Date(s.created_at).getTime();
            return t >= start.getTime() && t < end.getTime();
        });
    }, [sales]);

    const customMonthSales = useMemo(() => {
        if (!customMonth) return [];
        const [yy, mm] = customMonth.split("-").map(Number);
        const start = new Date(yy, mm - 1, 1, 0, 0, 0, 0);
        const end = new Date(yy, mm, 1, 0, 0, 0, 0);
        return sales.filter((s) => {
            const t = new Date(s.created_at).getTime();
            return t >= start.getTime() && t < end.getTime();
        });
    }, [sales, customMonth]);

    const prevMonthLabel = useMemo(() => {
        const d = new Date();
        const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        return `${GREEK_MONTHS[prev.getMonth()]} ${prev.getFullYear()}`;
    }, []);

    const customMonthLabel = useMemo(() => {
        if (!customMonth) return "";
        const [yy, mm] = customMonth.split("-").map(Number);
        return `${GREEK_MONTHS[mm - 1]} ${yy}`;
    }, [customMonth]);

    const visibleSales =
        range === "all"
            ? sales
            : range === "prev"
              ? prevMonthSales
              : range === "custom"
                ? customMonthSales
                : lastMonthSales;
    const summary = useMemo(() => {
        const totals = visibleSales.reduce(
            (acc, s) => {
                acc.gross += s.total_gross;
                acc.charged += s.amount_charged;
                acc.overage += s.overage || 0;
                return acc;
            },
            { gross: 0, charged: 0, overage: 0 }
        );
        return totals;
    }, [visibleSales]);

    const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

    if (!customer) return null;

    const limitSet = customer.period_limit_set !== false; // backward compat: undefined ⇒ true
    const limit = limitSet
        ? (customer.period_limit ?? customer.monthly_limit ?? MONTHLY_LIMIT)
        : 0;
    const remaining = limitSet ? (customer.remaining ?? limit) : 0;
    const used = customer.used_this_month ?? Math.max(0, limit - remaining);
    const usedPct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    const maxed = limitSet && remaining <= 0;
    const warn = limitSet && remaining <= 5 && !maxed;

    return (
        <div
            className="bg-white border-2 border-[#0A0A0A] mb-6"
            data-testid="customer-detail-panel"
        >
            {/* Header — identical to CustomerCardDialog */}
            <div className="p-7 border-b-2 border-[#0A0A0A] flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-1">
                        ΚΑΡΤΕΛΑ ΠΕΛΑΤΗ
                    </div>
                    <div className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-3 flex-wrap">
                        <Badge className="rounded-none bg-[#0A0A0A] text-white font-mono text-base px-3 py-1.5">
                            #{customer.code}
                        </Badge>
                        {customer.name}
                        {customer.is_demo && (
                            <span
                                className="text-xs font-black tracking-wider px-2 py-1 bg-[#A855F7] text-white"
                                data-testid="demo-badge-detail"
                            >
                                🧪 DEMO — ΔΟΚΙΜΑΣΤΙΚΟΣ
                            </span>
                        )}
                        {customer.no_credit_control && !customer.is_demo && (
                            <span
                                className="text-xs font-black tracking-wider px-2 py-1 bg-[#FF9500] text-white"
                                data-testid="vip-badge-detail"
                            >
                                🛡️ VIP — ΧΩΡΙΣ ΕΛΕΓΧΟ
                            </span>
                        )}
                        {customer.is_month_override && !customer.is_demo && !customer.no_credit_control && (
                            <span
                                className="text-xs font-black tracking-wider px-2 py-1 bg-[#5856D6] text-white"
                                data-testid="month-override-badge"
                                title="Ειδικό όριο μόνο για τον τρέχοντα μήνα"
                            >
                                🎯 ΕΙΔΙΚΟ ΟΡΙΟ ΤΡΕΧ. ΜΗΝΑ
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-[#F4F5F7] transition-colors"
                    data-testid="close-detail-panel"
                >
                    <X size={22} weight="bold" />
                </button>
            </div>

            {/* Info grid + QR (same as CustomerCardDialog) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-2 p-7 space-y-5 border-b lg:border-b-0 lg:border-r border-[#E2E4E9]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoField
                            icon={IdentificationCard}
                            label="ΑΦΜ"
                            value={customer.afm || "—"}
                            mono
                        />
                        <InfoField
                            icon={Phone}
                            label="ΤΗΛΕΦΩΝΟ"
                            value={customer.phone || "—"}
                            mono
                        />
                        <InfoField
                            icon={EnvelopeSimple}
                            label="EMAIL"
                            value={customer.email || "—"}
                        />
                        <InfoField
                            icon={MapPin}
                            label="ΘΕΣΗ"
                            value={customer.position || "—"}
                        />
                    </div>

                    {/* Balance card */}
                    {customer.is_demo ? (
                        <div
                            className="border-2 border-[#A855F7] bg-[#F5E9FF] p-5"
                            data-testid="card-balance-demo"
                        >
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#7E22CE]">
                                ΔΟΚΙΜΑΣΤΙΚΟΣ ΠΕΛΑΤΗΣ · ΟΡΙΟ 9.999€
                            </div>
                            <div className="font-heading text-3xl font-black tracking-tighter mt-1 text-[#7E22CE]">
                                🧪 9.999,00€
                            </div>
                            <div className="text-xs text-[#5C5F66] mt-1">
                                Οι κινήσεις του πελάτη DEMO ΔΕΝ καταγράφονται
                                στα reports και ΔΕΝ μετράνε στις πωλήσεις.
                            </div>
                        </div>
                    ) : (
                    <div
                        className={`border-2 p-5 ${
                            !limitSet
                                ? "border-[#5C5F66] bg-[#F4F5F7]"
                                : maxed
                                  ? "border-[#FF3B30] bg-[#FDEAE8]"
                                  : warn
                                    ? "border-[#FFCC00] bg-[#FFF8DD]"
                                    : "border-[#34C759] bg-[#EAF7EE]"
                        }`}
                        data-testid="card-balance"
                    >
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66]">
                            {customer.is_vip || customer.no_credit_control
                                ? "ΥΠΟΛΟΙΠΟ ΜΗΝΑ VIP (5.000€)"
                                : "ΥΠΟΛΟΙΠΟ ΜΗΝΑ"}
                        </div>
                        {!limitSet ? (
                            <>
                                <div className="font-heading text-2xl font-black tracking-tighter mt-1 text-[#5C5F66] uppercase">
                                    ΧΩΡΙΣ ΟΡΙΟ ΜΗΝΑ
                                </div>
                                <div className="text-xs text-[#5C5F66] mt-1">
                                    Δεν έχει οριστεί όριο πίστωσης για τον τρέχοντα μήνα. Καμία αγορά δεν επιτρέπεται.
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className={`font-heading text-5xl font-black tracking-tighter mt-1 ${
                                        maxed
                                            ? "text-[#B11919]"
                                            : warn
                                              ? "text-[#7A5C00]"
                                              : "text-[#1F8A3E]"
                                    }`}
                                >
                                    {fmtEUR(remaining)}
                                </div>
                                <div className="text-xs text-[#5C5F66] mt-1">
                                    Χρησιμοποιήθηκαν{" "}
                                    <strong className="text-[#0A0A0A]">{fmtEUR(used)}</strong>{" "}
                                    από {fmtEUR(limit)}
                                </div>
                                <div className="mt-3 h-2 bg-white border border-[#E2E4E9]">
                                    <div
                                        className={`h-full ${
                                            maxed
                                                ? "bg-[#FF3B30]"
                                                : warn
                                                  ? "bg-[#FFCC00]"
                                                  : "bg-[#002FA7]"
                                        }`}
                                        style={{ width: `${usedPct}%` }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    )}

                    {customer.info && (
                        <div className="border border-[#E2E4E9] p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] flex items-center gap-2 mb-2">
                                <Note size={12} weight="bold" /> ΠΛΗΡΟΦΟΡΙΕΣ
                            </div>
                            <div className="text-sm whitespace-pre-wrap text-[#1A1A1A]">
                                {customer.info}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: QR */}
                <div className="p-7 flex flex-col items-center justify-start bg-[#F4F5F7]">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] mb-3">
                        QR / BARCODE
                    </div>
                    <div className="bg-white border border-[#E2E4E9] p-5">
                        <QRCodeSVG
                            value={customer.qr_barcode || customer.code}
                            size={180}
                            level="M"
                            marginSize={1}
                        />
                    </div>
                    <div className="mt-3 text-center font-mono text-xs text-[#5C5F66] break-all max-w-[200px]">
                        {customer.qr_barcode || `#${customer.code}`}
                    </div>
                    <Button
                        onClick={() => window.print()}
                        variant="outline"
                        className="rounded-none mt-4 h-10 border-[#0A0A0A] font-bold"
                    >
                        ΕΚΤΥΠΩΣΗ
                    </Button>
                </div>
            </div>

            {/* Toolbar: range toggle + exports */}
            <div className="px-5 py-3 border-b border-[#E2E4E9] flex items-center justify-between gap-3 flex-wrap bg-[#FAFAFA]">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                        ΕΥΡΟΣ
                    </span>
                    <button
                        onClick={() => setRange("last")}
                        data-testid="range-month"
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 ${
                            range === "last"
                                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                : "bg-white text-[#5C5F66] border-[#E2E4E9]"
                        }`}
                    >
                        ΤΕΛΕΥΤΑΙΟΣ ΜΗΝΑΣ
                    </button>
                    <button
                        onClick={() => setRange("prev")}
                        data-testid="range-prev"
                        title={`Αγορές μήνα: ${prevMonthLabel}`}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 ${
                            range === "prev"
                                ? "bg-[#5856D6] text-white border-[#5856D6]"
                                : "bg-white text-[#5C5F66] border-[#E2E4E9]"
                        }`}
                    >
                        ΠΡΟΗΓΟΥΜΕΝΟΣ ΜΗΝΑΣ
                    </button>
                    <button
                        onClick={() => setRange("all")}
                        data-testid="range-all"
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 ${
                            range === "all"
                                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                : "bg-white text-[#5C5F66] border-[#E2E4E9]"
                        }`}
                    >
                        ΟΛΕΣ ΟΙ ΚΙΝΗΣΕΙΣ
                    </button>
                    <select
                        value={range === "custom" ? customMonth : ""}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (!v) {
                                setRange("last");
                                setCustomMonth("");
                            } else {
                                setCustomMonth(v);
                                setRange("custom");
                            }
                        }}
                        data-testid="range-custom-month"
                        title="Επίλεξε συγκεκριμένο μήνα"
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 cursor-pointer ${
                            range === "custom"
                                ? "bg-[#5856D6] text-white border-[#5856D6]"
                                : "bg-white text-[#5C5F66] border-[#E2E4E9]"
                        }`}
                    >
                        <option value="">📅 ΕΠΙΛΟΓΗ ΜΗΝΑ</option>
                        {pastMonths.map((m) => (
                            <option key={m.key} value={m.key}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() =>
                            exportCustomerPDF({ customer, sales: visibleSales })
                        }
                        disabled={visibleSales.length === 0}
                        data-testid="export-pdf-btn"
                        className="rounded-none h-10 bg-white border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold"
                    >
                        <FilePdf size={16} weight="bold" className="mr-2" />
                        EXPORT PDF
                    </Button>
                    <Button
                        onClick={() =>
                            exportCustomerXLSX({ customer, sales: visibleSales })
                        }
                        disabled={visibleSales.length === 0}
                        data-testid="export-xlsx-btn"
                        className="rounded-none h-10 bg-white border-2 border-[#1F8A3E] text-[#1F8A3E] hover:bg-[#1F8A3E] hover:text-white font-bold"
                    >
                        <FileXls size={16} weight="bold" className="mr-2" />
                        EXPORT EXCEL
                    </Button>
                </div>
            </div>

            {/* Summary numbers */}
            <div className="grid grid-cols-3 border-b border-[#E2E4E9]">
                <SummaryCell label="ΚΙΝΗΣΕΙΣ" value={visibleSales.length} />
                <SummaryCell label="ΣΥΝΟΛΟ" value={fmtEUR(summary.gross)} highlight />
                <SummaryCell
                    label="ΧΡΕΩΘΗΚΕ"
                    value={fmtEUR(summary.charged)}
                />
            </div>

            {/* Summary transactions table (no items) */}
            <div className="p-5">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2 flex items-center gap-2">
                    <Receipt size={14} weight="bold" />
                    ΣΥΝΟΛΙΚΕΣ ΚΙΝΗΣΕΙΣ{" "}
                    {range === "all"
                        ? ""
                        : range === "prev"
                          ? `(${prevMonthLabel})`
                          : range === "custom"
                            ? `(${customMonthLabel})`
                            : "(τελευταίος μήνας)"}
                </div>
                {loading ? (
                    <div className="py-8 text-center text-sm text-[#5C5F66]">
                        Φόρτωση...
                    </div>
                ) : visibleSales.length === 0 ? (
                    <div className="py-8 text-center text-sm text-[#5C5F66]">
                        Δεν υπάρχουν κινήσεις σε αυτό το διάστημα.
                    </div>
                ) : (
                    <>
                        <Table data-testid="summary-transactions-table">
                            <TableHeader>
                                <TableRow className="bg-[#0A0A0A] hover:bg-[#0A0A0A]">
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider">
                                        Ημερομηνια
                                    </TableHead>
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider text-center">
                                        Ειδη
                                    </TableHead>
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                        Καθαρη €
                                    </TableHead>
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                        ΦΠΑ €
                                    </TableHead>
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                        Συνολο €
                                    </TableHead>
                                    <TableHead className="text-white text-[10px] uppercase tracking-wider text-right">
                                        Χρεωθηκε €
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleSales.map((s) => {
                                    const isReturn = !!s.is_return;
                                    return (
                                    <TableRow
                                        key={s.id}
                                        className={isReturn ? "bg-[#FDEAE8]" : ""}
                                    >
                                        <TableCell className="font-mono text-xs">
                                            {fmtDate(s.created_at)}
                                            {isReturn && (
                                                <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-black bg-[#8B2530] text-white align-middle">
                                                    ΕΠΙΣΤΡΟΦΗ
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {s.line_items.length}
                                        </TableCell>
                                        <TableCell className={`text-right font-mono ${isReturn ? "text-[#8B2530]" : ""}`}>
                                            {fmtEUR(s.subtotal_net)}
                                        </TableCell>
                                        <TableCell className={`text-right font-mono ${isReturn ? "text-[#8B2530]" : ""}`}>
                                            {fmtEUR(s.total_vat)}
                                        </TableCell>
                                        <TableCell className={`text-right font-mono font-bold ${isReturn ? "text-[#8B2530]" : ""}`}>
                                            {fmtEUR(s.total_gross)}
                                        </TableCell>
                                        <TableCell className={`text-right font-mono ${isReturn ? "text-[#8B2530]" : ""}`}>
                                            {fmtEUR(s.amount_charged)}
                                            {s.overage > 0 && (
                                                <span className="text-[10px] text-[#B11919] ml-1">
                                                    (+{fmtEUR(s.overage)})
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {/* Detailed expandable cards */}
                        <div className="mt-6">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2">
                                ΑΝΑΛΥΤΙΚΗ ΚΑΡΤΕΛΑ — ΕΙΔΗ ΑΝΑ ΚΙΝΗΣΗ
                            </div>
                            <div className="space-y-2">
                                {visibleSales.map((s) => {
                                    const open = !!expanded[s.id];
                                    return (
                                        <div
                                            key={s.id}
                                            data-testid={`detail-row-${s.id}`}
                                            className="border border-[#E2E4E9]"
                                        >
                                            <button
                                                onClick={() => toggle(s.id)}
                                                className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-[#F4F5F7]"
                                            >
                                                <span className="font-mono text-xs">
                                                    {fmtDate(s.created_at)}
                                                </span>
                                                <span className="flex items-center gap-3 text-sm">
                                                    <span className="text-[#5C5F66]">
                                                        {s.line_items.length} είδη
                                                    </span>
                                                    <span className="font-bold">
                                                        {fmtEUR(s.total_gross)}
                                                    </span>
                                                    {open ? (
                                                        <CaretUp size={14} weight="bold" />
                                                    ) : (
                                                        <CaretDown size={14} weight="bold" />
                                                    )}
                                                </span>
                                            </button>
                                            {open && (
                                                <div className="border-t border-[#E2E4E9] bg-[#FAFAFA] p-3">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-[10px] uppercase text-[#5C5F66] border-b border-[#E2E4E9]">
                                                                <th className="text-left py-1.5 font-bold">
                                                                    Κωδικός
                                                                </th>
                                                                <th className="text-left py-1.5 font-bold">
                                                                    Περιγραφή
                                                                </th>
                                                                <th className="text-center py-1.5 font-bold">
                                                                    Ποσ.
                                                                </th>
                                                                <th className="text-right py-1.5 font-bold">
                                                                    Τιμή
                                                                </th>
                                                                <th className="text-center py-1.5 font-bold">
                                                                    ΦΠΑ%
                                                                </th>
                                                                <th className="text-right py-1.5 font-bold">
                                                                    Σύνολο
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {s.line_items.map((li, i) => (
                                                                <tr
                                                                    key={i}
                                                                    className="border-b border-[#E2E4E9]/60"
                                                                >
                                                                    <td className="py-1.5 font-mono">
                                                                        {li.item_code}
                                                                    </td>
                                                                    <td className="py-1.5">
                                                                        {li.description}
                                                                    </td>
                                                                    <td className="py-1.5 text-center font-mono">
                                                                        {li.quantity}
                                                                    </td>
                                                                    <td className="py-1.5 text-right font-mono">
                                                                        {fmtEUR(li.unit_price)}
                                                                    </td>
                                                                    <td className="py-1.5 text-center text-[#5C5F66]">
                                                                        {li.vat_rate}%
                                                                    </td>
                                                                    <td className="py-1.5 text-right font-mono font-bold">
                                                                        {fmtEUR(
                                                                            li.unit_price *
                                                                                li.quantity
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function InfoField({ icon: Icon, label, value, mono }) {
    return (
        <div className="border border-[#E2E4E9] p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C5F66] flex items-center gap-2 mb-1">
                <Icon size={12} weight="bold" />
                {label}
            </div>
            <div
                className={`text-sm text-[#0A0A0A] break-all ${mono ? "font-mono" : ""}`}
            >
                {value}
            </div>
        </div>
    );
}

function SummaryCell({ label, value, highlight }) {
    return (
        <div className="px-5 py-4 border-r border-[#E2E4E9] last:border-r-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                {label}
            </div>
            <div
                className={`mt-1 font-heading font-black tracking-tighter ${
                    highlight ? "text-3xl text-[#002FA7]" : "text-2xl text-[#0A0A0A]"
                }`}
            >
                {value}
            </div>
        </div>
    );
}
